import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchSpecifications as fetchSpecificationsApi,
  saveItemSpecifications,
  updateItemSpecifications,
  deleteItemSpecifications,
} from 'src/services/client';
import { SuccessMessages, ErrorMessages } from 'src/common/constants';

export interface Specification {
  id: string;
  title: string;
  description: string;
  catalogId?: string;
  userId?: string;
  [key: string]: any;
}

export interface SpecificationSavePayload {
  title: string;
  description: string;
  catalogId: string;
  userId: string;
  id?: string;
}

export interface UseSpecificationsReturn {
  specifications: Specification[];
  allSpecifications: Specification[];
  loading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  fetchSpecifications: (itemId: string) => Promise<void>;
  saveSpecification: (payload: SpecificationSavePayload) => Promise<boolean>;
  updateSpecification: (payload: SpecificationSavePayload) => Promise<boolean>;
  deleteSpecification: (id: string) => Promise<boolean>;
}

const success = (message: string): void => {
  toast.success(message, { autoClose: 10000, position: 'top-center' });
};

const failure = (message: string): void => {
  toast.error(message, { autoClose: 10000, position: 'top-center' });
};

export const useSpecifications = (itemId?: string): UseSpecificationsReturn => {
  const [specifications, setSpecifications] = useState<Specification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filtered: Specification[] = useMemo(() => {
    const term: string = searchTerm.trim().toLowerCase();

    if (!term) {
      return specifications;
    }

    return specifications.filter(
      (spec: Specification) =>
        spec.title?.toLowerCase().includes(term) ||
        spec.description?.toLowerCase().includes(term)
    );
  }, [specifications, searchTerm]);

  const fetchSpecifications = async (id: string): Promise<void> => {
    setLoading(true);

    try {
      const response: any = await fetchSpecificationsApi(id);

      if (response?.status === 200) {
        const json: any = await response.json();
        if (json?.statusCode === 200) {
          setSpecifications(json.data || []);
        } else {
          setSpecifications([]);
        }
      } else {
        setSpecifications([]);
      }
    } catch (err) {
      console.error('useSpecifications fetch error:', err);
      setSpecifications([]);
    } finally {
      setLoading(false);
    }
  };

  const saveSpecification = async (
    payload: SpecificationSavePayload
  ): Promise<boolean> => {
    setIsSaving(true);

    try {
      const response: any = await saveItemSpecifications(payload);

      if (response?.status === 200) {
        const json: any = await response.json();

        if (json?.statusCode === 200) {
          setSpecifications((prev) => [...prev, json.data]);
          success(SuccessMessages.specificationSaved);
          return true;
        }
      }

      failure(ErrorMessages.specificationFailed);
      return false;
    } catch (err) {
      console.error('useSpecifications save error:', err);
      failure(ErrorMessages.error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const updateSpecification = async (
    payload: SpecificationSavePayload
  ): Promise<boolean> => {
    setIsSaving(true);

    try {
      const response: any = await updateItemSpecifications(payload);

      if (response?.status === 200) {
        const json: any = await response.json();

        if (json?.statusCode === 200) {
          setSpecifications((prev) =>
            prev.map((spec: Specification) =>
              spec.id === json.data?.id ? json.data : spec
            )
          );
          success(SuccessMessages.specificationUpdated);
          return true;
        }
      }

      failure(ErrorMessages.specificationUpdateFailed);
      return false;
    } catch (err) {
      console.error('useSpecifications update error:', err);
      failure(ErrorMessages.error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSpecification = async (id: string): Promise<boolean> => {
    setIsDeleting(true);

    try {
      const response: any = await deleteItemSpecifications(id);

      if (response?.status === 200) {
        const json: any = await response.json();

        if (json?.statusCode === 200) {
          setSpecifications((prev) =>
            prev.filter((spec: Specification) => spec.id !== id)
          );
          success(SuccessMessages.specificationDeleted);
          return true;
        }
      }

      failure(ErrorMessages.specificationDeleteFailed);
      return false;
    } catch (err) {
      console.error('useSpecifications delete error:', err);
      failure(ErrorMessages.error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!itemId) {
      return;
    }

    void fetchSpecifications(itemId);
  }, [itemId]);

  return {
    specifications: filtered,
    allSpecifications: specifications,
    loading,
    isSaving,
    isDeleting,
    searchTerm,
    setSearchTerm,
    fetchSpecifications,
    saveSpecification,
    updateSpecification,
    deleteSpecification,
  };
};
